import { Link } from 'react-router-dom';
import {
  Layout, Typography, Menu, Avatar, Row, message, Spin, Col, Alert,
} from 'taxaroo-ui';
import config from '~src/components/helpers/config';
import Logo from '~src/assets/images/taxaroo_logo.svg';
import { useCreateSubscriptionMutation, useUpdateSubscriptionMutation } from '~src/graphql/mutations/settings';
import { useCreateBillingPortalSessionMutation } from '~src/graphql/mutations/stripeInformation';
import { useGetSubscriptionQuery } from '~src/graphql/queries/settings';
import { useGetStripeCustomerQuery } from '~src/graphql/queries/stripeInformation';
import { useAppDispatch, useAppSelector } from '~src/redux/hooks';
import { resetChat } from '~src/redux/slices/chatSlice';
import { resetClientData } from '~src/redux/slices/clientSlice';
import { resetInterviews } from '~src/redux/slices/interviewsSlice';
import { resetSession } from '~src/redux/slices/sessionSlice';
import StripePricingTable from '~src/components/organisms/StripePricingTable';
import ButtonWithText from './ButtonWithText';

const { Header } = Layout;
const { Title, Paragraph } = Typography;
const { SubMenu, Item } = Menu;

interface PayWallProps {
  customerId: string;
}
const PayWall = ({
  customerId,
}: PayWallProps) => {
  const dispatch = useAppDispatch();
  const {
    email,
    firstName,
    firmAccountId,
    userRole,
  } = useAppSelector((state) => state.session);
  const firstLetter = firstName?.charAt(0)?.toUpperCase() || '?';

  // graphql
  // Get subscription info query
  const { data: subscriptionData, loading: loadingSubscription } = useGetSubscriptionQuery();
  // we also want to get the customer, because we need to check the customre for a default payment method
  const { data: customerData, loading: loadingCustomer } = useGetStripeCustomerQuery({
    variables: {
      getStripeCustomerInput: {
        customerId,
      },
    },
    fetchPolicy: 'network-only',
  });
  const customer = customerData?.getStripeCustomer;
  // update subscription
  const [updateSubscritpion, { loading: loadingUpdateSubscription }] = useUpdateSubscriptionMutation();
  // create subscription
  const [createSubscritpion, { loading: loadingCreateSubscription }] = useCreateSubscriptionMutation();
  // Create billing portal session mutation
  const [createBillingPortalSession, { loading: loadingBillingPortal }] = useCreateBillingPortalSessionMutation({
    onError: (err) => {
      message.error(err.message);
    },
  });

  const onClick = ({ key }: { key: string }) => {
    if (key === 'logout') {
      dispatch(resetSession());
      dispatch(resetClientData());
      dispatch(resetInterviews());
      dispatch(resetChat());
    } else if (key === 'help') {
      window.open('https://help.taxaroo.com/', '_blank', 'noreferrer');
    }
  };

  const handleRenewPlan = async () => {
    if (loadingSubscription) {
      message.info('Loading subscription information. Please wait a couple seconds and try again.');
      return;
    }
    if (subscriptionData?.GetSubscriptionByContext?.status === 'active' && subscriptionData?.GetSubscriptionByContext?.cancel_at_period_end) {
      // update subscription to cancel at period end to false
      await updateSubscritpion({
        variables: {
          updateStripeSubscriptionInput: {
            cancel_at_period_end: false,
          },
        },
        refetchQueries: ['GetSubscriptionByContext', 'isActiveFirm'],
        onCompleted: () => {
          message.success('Subscription reactivated successfully');
        },
        onError: (error) => {
          message.error(`Subscription could not be reactivated. ${error.message}`);
        },
      });
    } else if (subscriptionData?.GetSubscriptionByContext?.status === 'canceled') {
      // we need to start a whole new subscription
      await createSubscritpion({
        variables: {
          createStripeSubscriptionInput: {
            customerId: subscriptionData?.GetSubscriptionByContext?.customer,
            planId: subscriptionData?.GetSubscriptionByContext?.items.data[0].plan.id,
            quantity: subscriptionData?.GetSubscriptionByContext?.quantity,
            firmAccountId,
          },
        },
        refetchQueries: ['GetSubscriptionByContext', 'isActiveFirm'],
        onCompleted: () => {
          message.success('Subscription reactivated successfully');
        },
        onError: (error) => {
          message.error(`Subscription could not be reactivated. ${error.message}`);
        },
      });
    }
  };

  const handleAddPaymentMethod = () => {
    createBillingPortalSession({
      variables: {
        createBillingPortalInput: {
          customer: customerId,
          flow_data: {
            type: 'payment_method_update',
            after_completion: {
              type: 'hosted_confirmation',
              hosted_confirmation: {
                custom_message: 'Your payment method has been successfully added!',
              },
            },
          },
          return_url: window.location.href,
        },
      },
    }).then(({ data }) => {
      window.location.href = data?.createBillingPortalSession;
    });
  };

  const handleGoToBillingPortal = async () => {
    const { data } = await createBillingPortalSession({
      variables: {
        createBillingPortalInput: {
          customer: customerId,
          return_url: window.location.href,
        },
      },
    });
    window.location.href = data.createBillingPortalSession;
  };

  const noDefaultPaymentMethod = !customer?.invoice_settings?.default_payment_method;

  if (loadingCustomer || loadingSubscription) {
    return (
      <Row justify="center" align="middle" style={{ height: '100vh' }}>
        <Spin size="large" />
      </Row>
    );
  }

  const recordsShow = () => {
    // if the subscription is past due and there is a payment method on file
    if (subscriptionData?.GetSubscriptionByContext?.status === 'past_due' && (customer?.default_source || !noDefaultPaymentMethod)) {
      return 'Our records show that there was difficulty processing your payment. Please update your payment method below.';
    }
    // if there is no payment method on file
    if (noDefaultPaymentMethod) {
      return 'Our records show you do not have a default payment method on file. Please add a payment method below.';
    }
    // if the subscription is canceled
    if (subscriptionData?.GetSubscriptionByContext?.status === 'canceled') {
      return 'Our records show that your subscription may have been canceled, or there was difficulty processing your payment. Please reactivate your subscription below.';
    }
    return '';
  };

  return (
    <Layout>
      <Header style={{ display: 'flex', justifyContent: 'space-between', height: '70px' }}>
        <Link to="/">
          <Logo style={{ width: '150px', height: '35px', margin: '15px 13px' }} />
        </Link>
        <div style={{ width: '100%' }}>
          <Menu
            mode="horizontal"
            theme="light"
            onClick={onClick}
            selectable={false}
            style={{ display: 'flex' }}
          >
            <SubMenu
              icon={(
                <Avatar>
                  {firstLetter.toUpperCase()}
                </Avatar>
              )}
            >
              <Item key="help">
                Help Center
              </Item>
              <Item key="logout">
                Logout
              </Item>
            </SubMenu>
          </Menu>
        </div>
      </Header>
      <Layout style={{
        minHeight: 'calc(100vh - 70px)', display: 'flex', alignItems: 'center', backgroundColor: '#fff', paddingBottom: 40,
      }}
      >

        <Row style={{ maxWidth: 760, padding: 20 }}>
          <Title level={2} style={{ color: 'rgb(13, 87, 165)', margin: '10px 0 0 0', textAlign: 'center' }}>
            {subscriptionData?.GetSubscriptionByContext ? 'Oops! Your subscription is no longer active' : 'Oops! You do not currently have a subscription with Taxaroo'}
          </Title>
          <Paragraph>
            {recordsShow()}
          </Paragraph>
          <Paragraph>
            If you need any help, please don’t hesitate to reach out to our support team in the lower right-hand corner of your screen.
            We’re here to assist you!
          </Paragraph>
        </Row>

        {userRole !== 'STAFF'
          ? (
            <Col style={{ textAlign: 'center', width: '100%' }}>
              {subscriptionData?.GetSubscriptionByContext && (
                <div style={{ marginTop: 60 }}>
                  <Row
                    gutter={16}
                    style={{
                      justifyContent: 'center', maxWidth: 900, margin: 'auto', marginBottom: 50, gap: 40,
                    }}
                  >
                    <Col style={{ maxWidth: 400 }}>
                      <div>
                        <ButtonWithText
                          onPress={handleGoToBillingPortal}
                          title="View Billing Portal"
                          description="Go to your billing portal to update your payment information or view past invoices."
                          buttonText="Go to Billing Portal"
                          buttonProps={{
                            type: 'default',
                            loading: loadingBillingPortal,
                          }}
                        />
                      </div>
                    </Col>
                    <Col style={{ maxWidth: 400 }}>
                      {noDefaultPaymentMethod
                        ? (
                          <ButtonWithText
                            onPress={handleAddPaymentMethod}
                            title="Update Payment Method"
                            description="To reactivate your subscription please add a payment method."
                            buttonText="Update Payment Method"
                            buttonProps={{
                              type: 'primary',
                            }}
                          />
                        )
                        : (
                          <ButtonWithText
                            onPress={handleRenewPlan}
                            title="Reactivate Plan"
                            description="Reinstate your subscription and continue where you left off."
                            buttonText="Reactivate Plan"
                            buttonProps={{
                              type: 'primary',
                              loading: loadingUpdateSubscription || loadingCreateSubscription,
                            }}
                          />
                        )}
                    </Col>
                  </Row>
                </div>
              )}
              <Title level={3} style={{ textAlign: 'center' }}>
                {subscriptionData?.GetSubscriptionByContext ? 'Or choose a plan' : 'Choose a plan'}
              </Title>
              <div style={{ display: 'block', width: '100%' }}>
                <StripePricingTable id={config.stripePaywallPricingTableId} email={email} />
              </div>
            </Col>
          )
          : (
            <Col style={{ textAlign: 'center', marginTop: 20 }}>
              <Alert
                message="Your subscription is no longer active. Please contact your firm administrator to reactivate your subscription."
                type="warning"
                showIcon
              />
            </Col>
          )}
      </Layout>
    </Layout>
  );
};

export default PayWall;
